import { sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import {
  renderNameCell,
  renderCategoryCell,
  renderStandardHeader,
  renderInputCell,
  renderInputHeader,
  renderEditInputCell,
} from '../datagrid/customCellRenders';

import { CHECK_IF_INTEGER_REGEXP } from '@commons/utils/regex';
import { getUniqueValuesByKey } from '@commons/utils/dataGrid';
import { ProgressCircle } from '@commons/ProgressCircle';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

import { TotalContainer } from '../datagrid/styledComponents';

const getTimeSlotsColumns = (isPTSCellEditable, productionTimeSlots, { dayColumnFields }) => {
  const columns = [];

  const sortedProductionTimeSlots = sortBy(productionTimeSlots, 'startTime');

  sortedProductionTimeSlots.forEach((pts) => {
    const formattedStartTime = moment(
      pts.startTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);
    const formattedEndTime = moment(
      pts.endTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);

    columns.push({
      ...dayColumnFields,
      field: pts.id,
      headerName: pts.name,
      headerSubTitle: `${formattedStartTime} - ${formattedEndTime}`,
      editable: isPTSCellEditable,
      disableColumnMenu: true,
      sortable: false,
      resizable: true,
      renderEditCell: renderEditInputCell,
      renderHeader: renderInputHeader,
      preProcessEditCellProps: ({ props }) => {
        if (!props.value) {
          // No need to check if it's an integer when it's null
          return { ...props, error: false };
        }

        // Not using Number.isInteger() because it allows numbers like 1.0 or 1. without anything behind
        const hasError = !new RegExp(CHECK_IF_INTEGER_REGEXP).test(`${props.value}`);
        return { ...props, error: hasError };
      },
    });
  });

  return columns;
};

export const generateTimeSlotsColumns = ({
  productionTimeSlots = [],
  editable = true,
  rowAnimations = {},
} = {}) => {
  const dayColumnFields = {
    type: 'number',
    minWidth: 148,
    disableReorder: true,
    filterable: false,
    headerAlign: 'center',
    align: 'center',
    renderCell: (props) => renderInputCell({ isEditable: editable, ...props }, rowAnimations),
  };

  return getTimeSlotsColumns(editable, productionTimeSlots, { dayColumnFields });
};

const TotalCell = ({ totalRatio }) => (
  <TotalContainer>
    <ProgressCircle customSize={40} percentage={totalRatio} useIsComplete={false} />
  </TotalContainer>
);

export const getColumns = (
  productionTimeSlots = [],
  products = [],
  rowAnimations = {},
  canEditAllocation = false,
) => {
  const commonColumnsProps = {
    disableReorder: true,
    renderHeader: (props) => renderStandardHeader(props, { paddingRight: '8px' }),
  };

  const nameColumn = {
    ...commonColumnsProps,
    field: 'name',
    headerName: i18next.t('GENERAL.NAME'),
    minWidth: 320,
    renderCell: ({ row }) =>
      renderNameCell(row.name, { sku: row.sku, withImg: true, imgSrc: row.img }),
  };

  const categoryColumn = {
    ...commonColumnsProps,
    field: 'category',
    headerName: i18next.t('GENERAL.CATEGORY'),
    minWidth: 200,
    renderCell: ({ row }) => renderCategoryCell(row.category),
    filterable: true,
    type: 'singleSelect',
    valueOptions: () => {
      const uniqueCategories = getUniqueValuesByKey(products, 'category');

      // Products categories already contains translated "None" value
      return uniqueCategories;
    },
    getApplyQuickFilterFn: (value) => {
      if (!value) {
        return () => true;
      }
      return (params) => {
        const categoryValue = params.value;
        const searchValue = value.toLowerCase();

        // Handle null/undefined values - show them when searching for "none" or the translated "none" value
        if (!categoryValue) {
          const translatedNoneValue = i18next.t('GENERAL.NONE_VALUE').toLowerCase();
          return translatedNoneValue.includes(searchValue);
        }

        // Handle regular category values
        return categoryValue.toLowerCase().includes(searchValue);
      };
    },
  };

  const totalColumn = {
    ...commonColumnsProps,
    field: 'total',
    type: 'number',
    headerName: i18next.t('GENERAL.TOTAL'),
    minWidth: 100,
    headerAlign: 'center',
    filterable: false,
    renderCell: ({ row }) => <TotalCell totalRatio={row.total} />,
  };

  // Only used by filters
  const skuColumn = {
    field: 'sku',
    headerName: i18next.t('GENERAL.SKU'),
  };

  const subCategoryColumn = {
    field: 'subCategory',
    headerName: i18next.t('GENERAL.SUB_CATEGORY'),
    type: 'singleSelect',
    valueOptions: () => {
      const uniqueSubCategories = getUniqueValuesByKey(products, 'subCategory');

      // Products sub-categories already contains translated "None" value
      return uniqueSubCategories;
    },
  };

  const launchDateColumn = {
    field: 'launchDate',
    headerName: i18next.t('ADMIN.STORES.LIST_COLUMN_LAUNCH_DATE'),
  };

  const endDateColumn = {
    field: 'endDate',
    headerName: i18next.t('ADMIN.STORES.LIST_COLUMN_END_DATE'),
  };

  return [
    nameColumn,
    skuColumn,
    categoryColumn,
    ...generateTimeSlotsColumns({
      productionTimeSlots,
      rowAnimations,
      editable: canEditAllocation,
    }),
    totalColumn,
    subCategoryColumn,
    launchDateColumn,
    endDateColumn,
  ];
};
